import { AutomatedContentBanner, getContentToDisplay } from "@2u-landing-pages/components/AutomatedContent";
import { Card } from "@2u-landing-pages/components/Card";
import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { ItemsContainer } from "@2u-landing-pages/components/ItemsContainer";
import { ProductCard } from "@2u-landing-pages/components/ProductCard";
import { TemplateWrapper } from "@2u-landing-pages/components/TemplateWrapper";
import { Tile } from "@2u-landing-pages/components/Tile";
import { VideoEmbed } from "@2u-landing-pages/components/VideoEmbed";
import { PricingApiContext, useProgramMeta } from "@2u-landing-pages/providers";
import cn from "classnames";
import React, { useContext } from "react";
import { Banner } from "../Banner";
import { Bento } from "../Bento";
import { Carousel } from "../Carousel";
import { DetailCallout } from "../DetailCallout";
import { Default as Form } from "../Form/variations/Default/Default";
import { Marquee } from "../Marquee";

import type {
  ContentfulImageConfig,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeFlexWithoutUnresolvableLinksResponse,
  TypeProduct_cardWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useMediaQuery } from "@2u-landing-pages/utils";
import type { SettingsExtended } from "../Carousel/Carousel.types";
import type { CustomCSSProperties, FlexProps, FlexSlotProps } from "./Flex.types";

type TemplateWrapperBackgroundImage = React.ComponentProps<typeof TemplateWrapper>["backgroundImage"];
// @ts-ignore
import styles from "./Flex.module.scss";

interface ComponentsMap {
  [key: string]: React.ComponentType<any>;
}

const desktopOverlapSpacing = {
  "Large (244/126)": "244",
  "Medium (212/108)": "212",
  "Small (180/90)": "180",
  "X-Small (148/72)": "148",
};

const mobileOverlapSpacing = {
  "Large (244/126)": "126",
  "Medium (212/108)": "108",
  "Small (180/90)": "90",
  "X-Small (148/72)": "72",
};

const componentsMap: ComponentsMap = {
  automated_content: AutomatedContentBanner,
  bento: Bento,
  carousel: Carousel,
  banner: Banner,
  detail_callout: DetailCallout,
  items_container: ItemsContainer,
  tile: Tile,
  content_full: ContentLayout,
  form: Form,
  card: Card,
  marquee: Marquee,
  video: VideoEmbed,
};

const FlexSlotRenderer: React.FC<FlexSlotProps> = ({
  slot,
  index,
  inspector,
  alignment,
  phoneNumber,
  isVertical,
  secondaryBackgroundColor,
  widthPercentage = 100,
  mode = "single-program",
}: FlexSlotProps) => {
  const isMobile = useMediaQuery("(max-width: 1023px)");

  if (!slot) return null;
  const contentType = slot?.sys?.contentType?.sys?.id;

  if (contentType === "product_card") {
    const product = slot as TypeProduct_cardWithoutUnresolvableLinksResponse;

    return (
      <div
        className={cn(styles[`slot${index + 1}`], {
          [styles[alignment]]: Boolean(alignment) && !isVertical,
          [styles.productCardInFlex]: true,
        })}
      >
        <ProductCard
          key={product.sys.id}
          ref={(el) => {
            if (el) {
              el.dataset.location = "Flex";
              el.dataset.position = index.toString();
            }
          }}
          product={product}
          position={index + 1}
          mode={mode}
          standalone={true}
        />
      </div>
    );
  }

  const Component = componentsMap[contentType as keyof typeof componentsMap];
  if (!Component) return null;

  // Specific rendering for content full
  if (contentType === "content_full") {
    const { image, eyebrow, heading, subhead, body, image_aspect_ratio } = (
      slot as TypeContent_fullWithoutUnresolvableLinksResponse
    ).fields;
    const isImageOnly = Boolean(image && !eyebrow && !heading && !subhead && !body);

    return (
      <div
        className={cn(styles[`slot${index + 1}`], {
          [styles.alignmentContainer]: true,
          [styles.secondaryBackground]: Boolean(secondaryBackgroundColor),
          [styles.verticalAlignmentContainer]: isVertical,
          [styles.contentfulImageWrapper]: isImageOnly,
        })}
      >
        <ContentLayout
          {...slot?.fields}
          className={cn(styles.contentLayout, {
            [styles[alignment]]: Boolean(alignment) && !isVertical,
          })}
          content={slot as TypeContent_fullWithoutUnresolvableLinksResponse}
          image_aspect_ratio={image_aspect_ratio}
          phoneNumber={phoneNumber}
        />
      </div>
    );
  }

  const props = {
    ...slot?.fields,
    inspector,
    entry: slot,
    phoneNumber,
  };

  // Component-specific prop handling.
  if (contentType === "bento") {
    props.embedded = true;
  } else if (contentType === "content_full") {
    props.content = slot;
  } else if (contentType === "form") {
    props.inFlex = true;
    props.location = "Flex";
  } else if (contentType === "carousel") {
    const { items } = slot?.fields || {};
    const totalSlides = items?.length || 0;
    const greaterThanOne = totalSlides > 1;

    props.carouselLayoutOverride = "small";
    props.section_background_color = "";
    props.location = "Flex Carousel";

    if (widthPercentage === 100) {
      const isStaticGrid = totalSlides <= 4;
      return (
        <div
          className={cn(styles[`slot${index + 1}`], styles.carouselStandaloneInFlex, {
            [styles.carouselStandaloneStatic]: isStaticGrid,
          })}
        >
          {React.createElement(Component, props)}
        </div>
      );
    }

    props.className = styles.carouselFlexContainer;

    const baseSettings: SettingsExtended = {
      dots: false,
      prevArrow: <></>,
      nextArrow: <></>,
      infinite: true,
      speed: 500,
      slidesToShow: 3,
      slidesToScroll: 1,
      initialSlide: 0,
      draggable: true,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            infinite: greaterThanOne,
            draggable: greaterThanOne,
            slidesToShow: totalSlides >= 3 ? 3 : totalSlides,
          },
        },
        {
          breakpoint: 768,
          settings: {
            infinite: greaterThanOne,
            draggable: greaterThanOne,
            slidesToShow: 1,
          },
        },
      ],
    };

    // All of these settings will need to be fine tuned.
    if (widthPercentage === 30 || widthPercentage === 50) {
      baseSettings.slidesToShow = 1;
      baseSettings.enableControls = greaterThanOne;
      baseSettings.infinite = greaterThanOne;
      baseSettings.draggable = greaterThanOne;
    }

    if (widthPercentage === 70) {
      const greaterThanTwo = totalSlides > 2;
      baseSettings.slidesToShow = 2;
      // We will probably want a better way to manage showing and hiding controls.
      // It will likely revolve around passing custom css classes to the carousel component
      // with the necessary breakpoints.
      baseSettings.enableControls = greaterThanTwo || (greaterThanOne && isMobile);
      baseSettings.infinite = greaterThanTwo;
      baseSettings.draggable = greaterThanTwo;
    }

    props.settingsOverride = baseSettings;
    return (
      <div
        className={cn(styles[`slot${index + 1}`], styles.carouselInFlex, {
          [styles.multiCardCarousel]: greaterThanOne,
          [styles.controlsDisabled]: !baseSettings.enableControls,
        })}
      >
        {React.createElement(Component, props)}
      </div>
    );
  }

  return (
    <div
      className={cn(styles[`slot${index + 1}`], {
        [styles[alignment]]: Boolean(alignment) && !isVertical,
      })}
    >
      {React.createElement(Component, props)}
    </div>
  );
};

const getWidthPercentage = (
  splitConfig: string,
  slot: number,
  totalSlots: number,
  reverseOrderDesktop: boolean,
): number => {
  if (splitConfig === "100" || totalSlots === 1) return 100;

  const [left, right] = splitConfig.split("/");

  if (totalSlots === 2) {
    if (slot === 0) {
      return !reverseOrderDesktop ? Number(left) : Number(right);
    }

    return !reverseOrderDesktop ? Number(right) : Number(left);
  }

  if (totalSlots === 3) {
    if (slot === 0 || slot === 1) {
      return !reverseOrderDesktop ? Number(left) : Number(right);
    }

    return !reverseOrderDesktop ? Number(right) : Number(left);
  }

  return 100;
};

type SlotsComponents =
  | TypeFlexWithoutUnresolvableLinksResponse["fields"]["slots"]
  | TypeFlexWithoutUnresolvableLinksResponse["fields"]["slots_mobile"];

const filterSlots = (slots: SlotsComponents, metaStartDate?: string, pricingApiStartDate?: string): SlotsComponents => {
  return slots?.filter((slot) => {
    const contentType = slot?.sys?.contentType?.sys?.id;

    if (contentType === "automated_content") {
      const content = getContentToDisplay({
        ...slot?.fields,
        metaStartDate,
        pricingApiStartDate,
      });

      return content !== null;
    }

    return true;
  });
};

export const Flex: React.FC<FlexProps> = ({
  id,
  slots,
  slots_mobile,
  split_configuration = "50/50",
  vertical_gap = "Medium (48/24)",
  alignment = split_configuration === "100" ? "center-center" : "left-center",
  reverse_order_desktop = false,
  reverse_order_mobile = false,
  enable_overlap = false,
  desktop_overlap_column = "",
  section_background_image,
  image_config,
  section_background_color,
  secondary_background_color,
  section_top_padding,
  section_bottom_padding,
  overlap_spacing = "X-Small (148/72)",
  inspector,
  phoneNumber,
  mode = "single-program",
}) => {
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const displaySlots = isMobile && slots_mobile && slots_mobile.length > 0 ? slots_mobile : slots;
  const programMeta = useProgramMeta();
  const { pricingApiValues } = useContext(PricingApiContext);
  const filteredSlots = filterSlots(
    displaySlots ?? slots,
    programMeta?.fields?.start_date,
    pricingApiValues?.startDate,
  );
  const oneItem = filteredSlots?.length === 1;
  const twoItems = filteredSlots?.length === 2;
  const threeItems = filteredSlots?.length === 3;
  const singleColumn = filteredSlots?.length === 1 || split_configuration === "100";

  if (!filteredSlots || filteredSlots.length === 0) {
    return null;
  }

  const style: CustomCSSProperties = {};

  if (section_background_color && !section_background_image)
    style["--flex-background-color"] = `var(${section_background_color})`;
  if (secondary_background_color) style["--flex-secondary-background-color"] = `var(${secondary_background_color})`;

  style["--flex-desktop-overlap"] = overlap_spacing ? `${desktopOverlapSpacing[overlap_spacing]}px` : "148px";
  style["--flex-mobile-overlap"] = overlap_spacing ? `${mobileOverlapSpacing[overlap_spacing]}px` : "72px";

  const isVertical = oneItem || split_configuration === "100";

  return (
    <TemplateWrapper
      className={cn(styles.flexContainerWrapper, {
        [styles.overlap]: enable_overlap,
      })}
      backgroundImage={section_background_image as TemplateWrapperBackgroundImage}
      backgroundColor={section_background_color}
      image_config={image_config as unknown as ContentfulImageConfig}
      inspector={inspector}
      backgroundImageEntryId={id}
      backgroundImageFieldId="section_background_image"
      style={style}
      paddingTop={section_top_padding}
      paddingBottom={section_bottom_padding}
      padded
    >
      <div
        className={cn(styles.gridContainer, {
          [styles.oneItem]: oneItem,
          [styles.twoItems]: twoItems,
          [styles.threeItems]: threeItems,
          [styles.reverseOrderDesktop]: !oneItem && reverse_order_desktop,
          [styles.reverseOrderMobile]: !oneItem && reverse_order_mobile,
          [styles.split_50_50]: !oneItem && split_configuration === "50/50",
          [styles.split_30_70]: !oneItem && split_configuration === "30/70",
          [styles.split_70_30]: !oneItem && split_configuration === "70/30",
          [styles.split_100]: !oneItem && split_configuration === "100",
          [styles.overlap]: enable_overlap,
          [styles.overlapDesktopRight]: !singleColumn && enable_overlap && desktop_overlap_column === "Right",
          [styles.overlapDesktopLeft]: !singleColumn && enable_overlap && desktop_overlap_column === "Left",
          [styles.vertical_gap_large]: vertical_gap === "Large (64/32)",
          [styles.vertical_gap_medium]: vertical_gap === "Medium (48/24)",
          [styles.vertical_gap_small]: vertical_gap === "Small (32/16)",
          [styles.vertical_gap_xsmall]: vertical_gap === "X-Small (24/12)",
        })}
      >
        {threeItems && (
          <>
            <div className={styles.innerGrid}>
              {filteredSlots?.slice(0, 2).map((slot, index) => (
                <FlexSlotRenderer
                  key={slot?.sys?.id || index}
                  slot={slot}
                  index={index}
                  inspector={inspector}
                  alignment={alignment}
                  phoneNumber={phoneNumber}
                  isVertical={isVertical}
                  secondaryBackgroundColor={secondary_background_color}
                  widthPercentage={getWidthPercentage(split_configuration, index, 3, reverse_order_desktop)}
                  mode={mode}
                />
              ))}
            </div>
            {filteredSlots[2] && (
              <FlexSlotRenderer
                key={filteredSlots[2]?.sys?.id || 2}
                slot={filteredSlots[2]}
                index={2}
                inspector={inspector}
                alignment={alignment}
                phoneNumber={phoneNumber}
                isVertical={isVertical}
                secondaryBackgroundColor={secondary_background_color}
                widthPercentage={getWidthPercentage(split_configuration, 2, 3, reverse_order_desktop)}
                mode={mode}
              />
            )}
          </>
        )}
        {twoItems &&
          filteredSlots?.map((slot, index) => (
            <FlexSlotRenderer
              key={slot?.sys?.id || index}
              slot={slot}
              index={index}
              inspector={inspector}
              alignment={alignment}
              phoneNumber={phoneNumber}
              isVertical={isVertical}
              secondaryBackgroundColor={secondary_background_color}
              widthPercentage={getWidthPercentage(split_configuration, index, 2, reverse_order_desktop)}
              mode={mode}
            />
          ))}
        {oneItem && filteredSlots[0] && (
          <FlexSlotRenderer
            key={filteredSlots[0]?.sys?.id || 2}
            slot={filteredSlots[0]}
            index={0}
            inspector={inspector}
            alignment={alignment}
            phoneNumber={phoneNumber}
            isVertical={isVertical}
            secondaryBackgroundColor={secondary_background_color}
            mode={mode}
          />
        )}
      </div>
    </TemplateWrapper>
  );
};
